import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Building, MapPin, User, Calendar, AlertTriangle, 
  CheckCircle, TrendingUp, Plus, ExternalLink, Pencil, Trash2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  live: 'bg-green-100 text-green-800',
  opening_soon: 'bg-blue-100 text-blue-800',
  setup: 'bg-yellow-100 text-yellow-800',
  closed: 'bg-red-100 text-red-800'
};

const RISK_COLORS = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500'
};

export default function FranchiseStores() {
  const { token } = useAuth();
  const [stores, setStores] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showNewStoreModal, setShowNewStoreModal] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [newStoreForm, setNewStoreForm] = useState({
    venue_id: '',
    name: '',
    location: '',
    franchisee_name: '',
    store_format: 'standard',
    opening_date: ''
  });

  // Venues = SSOT for store names + addresses (managed in User Management →
  // Locations). The franchise add/edit dialogs must select from this list
  // only — no free-text store names anywhere outside that page.
  const fetchVenues = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/venues?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVenues(Array.isArray(data) ? data : (data.venues || []));
      }
    } catch (err) {
      // non-fatal
    }
  }, [token]);

  const fetchStores = useCallback(async () => {
    try {
      let url = `${API_URL}/api/franchise/stores`;
      if (statusFilter) {
        url += `?status=${statusFilter}`;
      }
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStores(data.stores);
      }
    } catch (err) {
      toast.error('Error loading stores');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  useEffect(() => {
    fetchStores();
    fetchVenues();
  }, [fetchStores, fetchVenues]);

  const createStore = async () => {
    if (!newStoreForm.name || !newStoreForm.location || !newStoreForm.franchisee_name) {
      toast.error('Please select a location and fill in the franchisee name');
      return;
    }
    // Guard: name must match an active venue
    const matchedVenue = venues.find(
      (v) => (v.short_name || v.name) === newStoreForm.name || v.name === newStoreForm.name
    );
    if (!matchedVenue) {
      toast.error('Location must be selected from the Locations list (User Management → Locations).');
      return;
    }

    try {
      // Only pass fields the backend expects — drop venue_id since the
      // store name is the canonical link to the venue.
      const { venue_id, ...payload } = newStoreForm; // eslint-disable-line no-unused-vars
      const params = new URLSearchParams(payload);
      const res = await fetch(`${API_URL}/api/franchise/stores?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Store created successfully');
        setShowNewStoreModal(false);
        setNewStoreForm({
          venue_id: '',
          name: '',
          location: '',
          franchisee_name: '',
          store_format: 'standard',
          opening_date: ''
        });
        fetchStores();
      } else {
        toast.error('Failed to create store');
      }
    } catch (err) {
      toast.error('Error creating store');
    }
  };

  // Open the edit modal pre-filled with this store's current values.
  const openEdit = (store) => {
    setEditingStore({
      id: store.id,
      name: store.name || '',
      location: store.location || '',
      franchisee_name: store.franchisee_name || '',
      store_format: store.store_format || 'standard',
      opening_date: store.opening_date || '',
      status: store.status || 'setup',
    });
  };

  const saveEdit = async () => {
    if (!editingStore?.id) return;
    if (!editingStore.name || !editingStore.location || !editingStore.franchisee_name) {
      toast.error('Name, location and franchisee name are required');
      return;
    }
    // Guard: name must match an active venue from the SSOT list.
    const matchedVenue = venues.find(
      (v) => (v.short_name || v.name) === editingStore.name || v.name === editingStore.name
    );
    if (!matchedVenue) {
      toast.error('Pick a location from the dropdown (managed in User Management → Locations).');
      return;
    }
    try {
      const { id, ...patch } = editingStore;
      const params = new URLSearchParams(
        Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== '' && v !== null && v !== undefined))
      );
      const res = await fetch(`${API_URL}/api/franchise/stores/${id}?${params}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Store updated');
        setEditingStore(null);
        fetchStores();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to update store');
      }
    } catch (err) {
      toast.error('Error updating store');
    }
  };

  // Soft-delete a store. Backend marks status=closed and the default list
  // hides closed stores, so the tile disappears from the FM view.
  const deleteStore = async (store) => {
    const ok = window.confirm(
      `Delete "${store.name}"?\n\nThe store will be closed and removed from the dashboard. ` +
      `Inspections, tasks and issues linked to it will be preserved for audit.`
    );
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/api/franchise/stores/${store.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success(`${store.name} removed`);
        fetchStores();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to delete store');
      }
    } catch (err) {
      toast.error('Error deleting store');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  // Stats
  const liveStores = stores.filter(s => s.status === 'live').length;
  const openingSoon = stores.filter(s => s.status === 'opening_soon').length;
  const inSetup = stores.filter(s => s.status === 'setup').length;

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 overflow-x-hidden" data-testid="stores-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">Franchise Stores</h1>
          <p className="text-sm text-slate-500">Manage all franchise store locations</p>
        </div>
        <div className="flex gap-2">
          <select
            className="px-3 py-2 border rounded-lg text-sm flex-1 sm:flex-none"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Stores</option>
            <option value="live">Live</option>
            <option value="opening_soon">Opening Soon</option>
            <option value="setup">In Setup</option>
            <option value="closed">Closed (deleted)</option>
          </select>
          <Button 
            className="bg-red-600 hover:bg-red-700 text-sm whitespace-nowrap"
            onClick={() => setShowNewStoreModal(true)}
          >
            <Plus size={16} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Add Store</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 md:gap-4">
        <Card>
          <CardContent className="p-3 md:p-4 flex items-center gap-2 md:gap-3">
            <div className="p-1.5 md:p-2 bg-green-100 rounded-lg text-green-600">
              <CheckCircle size={18} className="md:w-6 md:h-6" />
            </div>
            <div>
              <p className="text-xl md:text-2xl font-bold">{liveStores}</p>
              <p className="text-xs md:text-sm text-gray-500">Live</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4 flex items-center gap-2 md:gap-3">
            <div className="p-1.5 md:p-2 bg-blue-100 rounded-lg text-blue-600">
              <Calendar size={18} className="md:w-6 md:h-6" />
            </div>
            <div>
              <p className="text-xl md:text-2xl font-bold">{openingSoon}</p>
              <p className="text-xs md:text-sm text-gray-500">Opening</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4 flex items-center gap-2 md:gap-3">
            <div className="p-1.5 md:p-2 bg-yellow-100 rounded-lg text-yellow-600">
              <Building size={18} className="md:w-6 md:h-6" />
            </div>
            <div>
              <p className="text-xl md:text-2xl font-bold">{inSetup}</p>
              <p className="text-xs md:text-sm text-gray-500">Setup</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stores Grid */}
      {stores.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            No stores found. Add your first store to get started.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {stores.map((store) => (
            <Card key={store.id} className="hover:shadow-md transition-shadow overflow-hidden">
              <CardContent className="p-3 md:p-4">
                <div className="flex justify-between items-start mb-2 md:mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full flex-shrink-0 ${RISK_COLORS[store.risk_level || 'green']}`} />
                    <h3 className="font-semibold text-sm md:text-base truncate">{store.name}</h3>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    <button
                      type="button"
                      onClick={() => openEdit(store)}
                      className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                      title="Edit store"
                      data-testid={`edit-store-${store.id}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteStore(store)}
                      className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                      title="Delete store"
                      data-testid={`delete-store-${store.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                    <Badge className={`${STATUS_COLORS[store.status]} text-xs`}>
                      {store.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
                
                <div className="space-y-1.5 text-xs md:text-sm">
                  <p className="flex items-center gap-2 text-gray-600 truncate">
                    <MapPin size={12} className="flex-shrink-0" />
                    <span className="truncate">{store.location}</span>
                  </p>
                  <p className="flex items-center gap-2 text-gray-600 truncate">
                    <User size={12} className="flex-shrink-0" />
                    <span className="truncate">{store.franchisee_name}</span>
                  </p>
                  {store.opening_date && (
                    <p className="flex items-center gap-2 text-gray-600">
                      <Calendar size={12} className="flex-shrink-0" />
                      Opening: {new Date(store.opening_date).toLocaleDateString()}
                    </p>
                  )}
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-3 gap-1 md:gap-2 mt-3 md:mt-4 pt-3 md:pt-4 border-t">
                  <div className="text-center" data-testid={`store-inspection-${store.id}`} title={store.last_inspection_date ? `Last inspection ${store.last_inspection_date}` + (store.last_inspector_name ? ` by ${store.last_inspector_name}` : '') : 'No inspection yet'}>
                    <p className={`text-base md:text-lg font-semibold ${
                      store.last_inspection_score == null ? 'text-gray-400' :
                      store.last_inspection_score >= 80 ? 'text-green-600' :
                      store.last_inspection_score >= 60 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {store.last_inspection_score != null ? `${Number(store.last_inspection_score).toFixed(1)}%` : '—'}
                    </p>
                    <p className="text-[10px] md:text-xs text-gray-500">
                      {store.last_inspection_date
                        ? `Insp · ${new Date(store.last_inspection_date).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}`
                        : 'Inspection'}
                    </p>
                  </div>
                  <div className="text-center" data-testid={`store-issues-${store.id}`} title={`${store.franchise_issues_open || 0} franchise + ${store.inspection_action_items_open || 0} inspection issues open`}>
                    <p className={`text-base md:text-lg font-semibold ${
                      (store.open_issues_count || 0) === 0 ? 'text-gray-400' : 'text-orange-600'
                    }`}>
                      {store.open_issues_count || 0}
                    </p>
                    <p className="text-[10px] md:text-xs text-gray-500">Issues</p>
                  </div>
                  <div className="text-center" data-testid={`store-tasks-${store.id}`} title={`${store.completed_tasks_count || 0} of ${store.total_tasks_count || 0} tasks completed`}>
                    <p className={`text-base md:text-lg font-semibold ${
                      (store.total_tasks_count || 0) === 0 ? 'text-gray-400' :
                      (store.open_tasks_count || 0) === 0 ? 'text-green-600' : 'text-blue-600'
                    }`}>
                      {store.total_tasks_count > 0
                        ? `${store.completed_tasks_count || 0}/${store.total_tasks_count}`
                        : '0'}
                    </p>
                    <p className="text-[10px] md:text-xs text-gray-500">
                      {store.total_tasks_count > 0 && (store.open_tasks_count || 0) === 0
                        ? 'All done'
                        : 'Tasks'}
                    </p>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2 mt-3 md:mt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 text-xs md:text-sm px-2 md:px-3"
                    onClick={() => window.location.href = `/franchise/issues/new?store=${store.id}`}
                  >
                    <AlertTriangle size={12} className="mr-1" />
                    <span className="hidden sm:inline">Report </span>Issue
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 text-xs md:text-sm px-2 md:px-3"
                    onClick={() => window.location.href = `/inspections?store=${encodeURIComponent(store.inspection_store_label || store.name)}`}
                    data-testid={`inspect-${store.id}`}
                  >
                    <TrendingUp size={12} className="mr-1" />
                    Inspect
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Store Modal */}
      {showNewStoreModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building size={20} />
                Add New Store
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Store / Location <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border rounded-lg px-3 py-2 bg-white"
                  value={newStoreForm.name}
                  onChange={(e) => {
                    const val = e.target.value;
                    const v = venues.find(x => (x.short_name || x.name) === val);
                    setNewStoreForm({
                      ...newStoreForm,
                      name: val,
                      venue_id: v?.id || '',
                      // Auto-fill location with the venue's address — user can override.
                      location: v?.address || newStoreForm.location,
                    });
                  }}
                  data-testid="new-store-name-select"
                >
                  <option value="">— Select a location —</option>
                  {venues.map((v) => {
                    const value = v.short_name || v.name;
                    return (
                      <option key={v.id || value} value={value}>
                        {v.name}{v.short_name && v.short_name !== v.name ? ` (${v.short_name})` : ''}
                      </option>
                    );
                  })}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Locations are managed in <a href="/admin/user-management?tab=locations" className="text-violet-600 underline">User Management → Locations</a>.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Address
                </label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 bg-slate-50"
                  placeholder="Pre-filled from the selected location"
                  value={newStoreForm.location}
                  onChange={(e) => setNewStoreForm({ ...newStoreForm, location: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Franchisee Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Owner/Operator name"
                  value={newStoreForm.franchisee_name}
                  onChange={(e) => setNewStoreForm({ ...newStoreForm, franchisee_name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Store Format</label>
                <select
                  className="w-full border rounded-lg px-3 py-2"
                  value={newStoreForm.store_format}
                  onChange={(e) => setNewStoreForm({ ...newStoreForm, store_format: e.target.value })}
                >
                  <option value="standard">Standard</option>
                  <option value="mini">Mini Store</option>
                  <option value="express">Express</option>
                  <option value="warehouse">Warehouse</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Opening Date</label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-2"
                  value={newStoreForm.opening_date}
                  onChange={(e) => setNewStoreForm({ ...newStoreForm, opening_date: e.target.value })}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={() => setShowNewStoreModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-red-600 hover:bg-red-700" 
                  onClick={createStore}
                >
                  Create Store
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Store Modal */}
      {editingStore && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditingStore(null)}>
          <Card className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Pencil size={20} />
                Edit Store
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Store / Location *</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 bg-white"
                  value={editingStore.name}
                  onChange={(e) => {
                    const val = e.target.value;
                    const v = venues.find(x => (x.short_name || x.name) === val);
                    setEditingStore({
                      ...editingStore,
                      name: val,
                      // If the picker is changed, refresh location to the new
                      // venue's address (user can still override below).
                      location: v?.address || editingStore.location,
                    });
                  }}
                  data-testid="edit-store-name"
                >
                  <option value="">— Select a location —</option>
                  {venues.map((v) => {
                    const value = v.short_name || v.name;
                    return (
                      <option key={v.id || value} value={value}>
                        {v.name}{v.short_name && v.short_name !== v.name ? ` (${v.short_name})` : ''}
                      </option>
                    );
                  })}
                  {/* If the saved name doesn't match any active venue (legacy
                      data), keep it visible so the user can re-pick. */}
                  {editingStore.name && !venues.find(v => (v.short_name || v.name) === editingStore.name) && (
                    <option value={editingStore.name} disabled>
                      {editingStore.name} (legacy — pick a current location)
                    </option>
                  )}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Locations are managed in <a href="/admin/user-management?tab=locations" className="text-violet-600 underline">User Management → Locations</a>.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address *</label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2"
                  value={editingStore.location}
                  onChange={(e) => setEditingStore({ ...editingStore, location: e.target.value })}
                  data-testid="edit-store-location"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Franchisee Name *</label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2"
                  value={editingStore.franchisee_name}
                  onChange={(e) => setEditingStore({ ...editingStore, franchisee_name: e.target.value })}
                  data-testid="edit-store-franchisee"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Store Format</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 bg-white"
                  value={editingStore.store_format}
                  onChange={(e) => setEditingStore({ ...editingStore, store_format: e.target.value })}
                >
                  <option value="express">Express</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Opening Date</label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-2"
                  value={editingStore.opening_date}
                  onChange={(e) => setEditingStore({ ...editingStore, opening_date: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 bg-white"
                  value={editingStore.status}
                  onChange={(e) => setEditingStore({ ...editingStore, status: e.target.value })}
                  data-testid="edit-store-status"
                >
                  <option value="setup">Setup</option>
                  <option value="opening_soon">Opening Soon</option>
                  <option value="live">Live</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditingStore(null)}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={saveEdit}
                  data-testid="save-edit-store"
                >
                  Save changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
